import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { GET_POSTS } from "./queries";

export default function Search() {
  const { searchTerm } = useParams<{ searchTerm: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [cursors, setCursors] = useState<(string | null)[]>([null]);
  const [posts, setPosts] = useState<any[]>([]);

  const perPage = 18;
  const searchFixed = searchTerm?.replace(/-/g, " ");
  const pageFromParams = searchParams.get("page");
  const currentPage = pageFromParams ? parseInt(pageFromParams, 10) : 1;

  const { loading, error, data, fetchMore } = useQuery(GET_POSTS, {
    variables: {
      search: searchFixed,
      after: cursors[currentPage - 1],
      first: perPage,
    },
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    if (data?.posts?.nodes) {
      setPosts(data.posts.nodes);
    }
  }, [data]);

  // Reset everything when a new search happens
  useEffect(() => {
    setPosts([]);
    setCursors([null]);
  }, [searchTerm]);

  const handleNextPage = async () => {
    // window.scrollTo({ top: 0, behavior: "smooth" });

    if (!data?.posts?.pageInfo?.hasNextPage) return;

    const { data: fetchMoreData } = await fetchMore({
      variables: {
        first: perPage,
        after: data.posts.pageInfo.endCursor,
      },
    });

    if (fetchMoreData?.posts.pageInfo.endCursor) {
      setCursors((prev) => [...prev, fetchMoreData.posts.pageInfo.endCursor]);
      setSearchParams({ page: (currentPage + 1).toString() });
    }
  };

  const handlePreviousPage = () => {
    // window.scrollTo({ top: 0, behavior: "smooth" });
    if (currentPage > 1) {
      setSearchParams({ page: (currentPage - 1).toString() });
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0a5640]"></div>
        <p className="mt-4 text-gray-600">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 font-bold">Error: {error.message}</p>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 py-8">
        <h2 className="text-2xl font-bold mb-8">Results for "{searchFixed}"</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((post: any) => (
            <Link
              to={`/post/${post.slug}`}
              className="no-underline text-inherit group"
              key={post.id}
            >
              <div className="p-4 border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow h-full bg-white">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="sm:w-5/12 flex-shrink-0">
                    <div className="image-wrapper relative pt-[100%] overflow-hidden rounded">
                      <div className="skeleton-loader absolute inset-0 bg-gray-200 animate-pulse"></div>
                      {post.featuredImage?.node?.mediaDetails?.sizes && (
                        <img
                          src={
                            post.featuredImage.node.mediaDetails.sizes.find(
                              (s: any) => s.name === "thumbnail"
                            )?.sourceUrl
                          }
                          alt="Post"
                          className="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-300"
                          onLoad={(e) => {
                            const wrapper =
                              e.currentTarget.closest(".image-wrapper");
                            if (wrapper) {
                              const loader = wrapper.querySelector(
                                ".skeleton-loader"
                              ) as HTMLElement;
                              if (loader) loader.style.display = "none";
                              e.currentTarget.style.opacity = "1";
                            }
                          }}
                        />
                      )}
                    </div>
                  </div>
                  <div className="sm:w-7/12 flex flex-col justify-center">
                    <h5
                      className="text-lg font-semibold mb-2 group-hover:text-[#0a5640] transition-colors line-clamp-2"
                      dangerouslySetInnerHTML={{
                        __html: post.title,
                      }}
                    />
                    {post.author?.node?.name && (
                      <p className="text-sm text-gray-600 mb-1">
                        <strong>Author:</strong> {post.author.node.name}
                      </p>
                    )}
                    <p className="text-sm text-gray-500">
                      {new Date(post.date).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="flex justify-center items-center mt-12 mb-8 gap-4">
          <button
            className="border border-[#0a5640] text-[#0a5640] hover:bg-[#0a5640] hover:text-white px-6 py-2 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={currentPage <= 1}
            onClick={handlePreviousPage}
          >
            Previous
          </button>
          <span className="text-lg font-medium">Page {currentPage}</span>
          <button
            className="border border-[#0a5640] text-[#0a5640] hover:bg-[#0a5640] hover:text-white px-6 py-2 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!data?.posts?.pageInfo?.hasNextPage}
            onClick={handleNextPage}
          >
            Next
          </button>
        </div>
      </div>
    </>
  );
}
